import { filter, isEmpty } from 'lodash';
import { ApolloQueryResult } from 'apollo-client';
import * as React from 'react';
import idx from 'idx';

import {
  ComposableTable,
  ColumnHeader,
  getDatestampCellGenerator,
  getGroupAvatarCellGenerator,
  getTextCellGenerator,
  // @ts-ignore
} from 'components/ComposableTable';
import Row from 'components/Row';
import ScrollLoadZone from 'components/ScrollLoadZone';
import Text from 'components/Text';

// @ts-ignore
import { Sort, SortDirection } from 'clients/api/organizationClasses';
import { ActiveGroupsQuery } from 'groups/containers/GroupList/queries/__generated__/ActiveGroupsQuery';
import { UnclaimedGroupsQuery } from 'groups/containers/GroupList/queries/__generated__/UnclaimedGroupsQuery';

import './GroupListTable.scss';

type UserInfo = {
  firstName: string | null;
  lastName: string | null;
};

type ActiveGroupRow = {
  uuid: string;
  className: string;
  createdAt: string;
  avatarSvgUrl: string;
  messagesCount: number;
  owners: UserInfo[];
  participantsCount: number;
};

type UnclaimedGroupRow = {
  uuid: string;
  className: string;
  createdAt: string;
  initials: string;
  owners: UserInfo[];
  participantsCount: number;
};

export type GroupRow = ActiveGroupRow | UnclaimedGroupRow;
export type GroupStatus = 'active' | 'unclaimed';

type Props = {
  groupStatus: GroupStatus;
  fetchMore: () => Promise<ApolloQueryResult<ActiveGroupsQuery | UnclaimedGroupsQuery>>;
  hasNextPage: boolean;
  loading: boolean;
  groups: GroupRow[];
  onGroupClicked: (row: GroupRow) => void;
  onSort: (sortColumn: Sort, sortDirection: SortDirection) => void;
  openGroupKey: string | null;
  query: string | null;
};

export function isActiveGroupRow(row: GroupRow): row is ActiveGroupRow {
  return 'avatarSvgUrl' in row;
}

export function isUnclaimedGroupRow(row: GroupRow): row is UnclaimedGroupRow {
  return 'initials' in row;
}

export default class GroupListTable extends React.Component<Props> {
  static defaultProps = {
    loading: true,
  };

  columnHeaderForColumn = (columnName: string): ColumnHeader => {
    const columnMap: { [s: string]: ColumnHeader } = {
      avatar: {
        name: '',
        type: 'avatar',
      },
      avatarInitials: {
        name: '',
        type: 'avatar',
      },
      announcements: {
        name: 'Announcements',
        width: 95,
      },
      dateCreated: {
        name: 'Date created',
        sortValue: 'created',
        width: 85,
      },
      dateRostered: {
        name: 'Date rostered',
        sortValue: 'created',
        width: 85,
      },
      name: {
        name: 'Name',
        sortValue: 'name',
      },
      owner: {
        name: 'Owner',
      },
      participants: {
        name: 'Participants',
        width: 70,
      },
    };

    return columnMap[columnName];
  };

  getHeaders = (): string[] => {
    const { groupStatus } = this.props;

    const columns = {
      active: ['avatar', 'name', 'owner', 'announcements', 'participants', 'dateCreated'],
      unclaimed: ['avatarInitials', 'name', 'owner', 'participants', 'dateRostered'],
    };

    return columns[groupStatus];
  };

  columnHeaders = (): Array<ColumnHeader> => {
    return this.getHeaders().map(name => this.columnHeaderForColumn(name));
  };

  generateOwnersText = (row: GroupRow) => {
    const firstOwner = idx(row, _ => _.owners[0]);

    if (!firstOwner) {
      return '';
    }

    const values = filter([firstOwner.firstName, firstOwner.lastName]);

    if (row.owners.length > 1) {
      values.push(`and ${row.owners.length - 1} more`);
    }

    return values.join(' ');
  };

  columnGenerators = (): Array<(data: Object) => React.ReactNode> => {
    const components: { [s: string]: (data: Object) => React.ReactNode } = {
      announcements: getTextCellGenerator('messagesCount'),
      avatar: getGroupAvatarCellGenerator({
        groupField: (row: ActiveGroupRow) => ({ groupAvatar: { svg_url: row.avatarSvgUrl } }),
      }),
      avatarInitials: getGroupAvatarCellGenerator({
        groupField: (row: UnclaimedGroupRow) => ({ initials: row.initials }),
      }),
      dateCreated: getDatestampCellGenerator('createdAt'),
      dateRostered: getDatestampCellGenerator('createdAt'),
      name: getTextCellGenerator('className'),
      owner: getTextCellGenerator((row: GroupRow) => this.generateOwnersText(row)),
      participants: getTextCellGenerator('participantsCount'),
    };

    return this.getHeaders().map(name => components[name]);
  };

  renderNoResultsView = () => {
    return (
      <Text align="center" size="medium" data-test="no-results-view">
        {this.props.query ? 'No classes match your search' : 'No classes'}
      </Text>
    );
  };

  render() {
    const {
      fetchMore,
      hasNextPage,
      groups,
      loading,
      onGroupClicked,
      onSort,
      openGroupKey,
    } = this.props;

    return (
      <div className="GroupListTable">
        <Row size="large">
          <ComposableTable
            columnGenerators={this.columnGenerators()}
            columnHeaders={this.columnHeaders()}
            handleSort={onSort}
            handleRowClick={onGroupClicked}
            keyField={(group: GroupRow) => group.uuid}
            keysOfActiveRows={openGroupKey ? [openGroupKey] : []}
            loading={loading}
            rows={groups || []}
            className="GroupListTable-table"
          />
        </Row>
        {!loading && hasNextPage && <ScrollLoadZone threshold={500} onZoneActivated={fetchMore} />}
        {!loading && isEmpty(groups) && this.renderNoResultsView()}
      </div>
    );
  }
}
